import type { Actor, CardId } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { AppIcons, Button } from '@web/ui/core/atoms';
import { useCardDetail, useClaimCard } from '@web/ui/core/hooks';
import { ClaimConfirmDialog } from '@web/ui/core/molecules';

/**
 * Banner shown above the conversation composer whenever the card is NOT
 * assigned to the current user — the AI, another human, or nobody is handling
 * it. Self-contained: resolves the card via `useCardDetail` and assuming via
 * `useClaimCard`. Renders nothing when the card is mine, closed, or unresolved.
 */
export function CardClaimBanner({
  cardId,
}: Readonly<{ cardId: CardId }>): React.JSX.Element | null {
  const detailQuery = useCardDetail(cardId);
  const claim = useClaimCard(cardId);
  const detail = detailQuery.data;

  if (detail?.closure !== null || detail.assignedToMe) return null;

  const { title, subtitle } = bannerText(detail.assigneeAgent, detail.agentDisplayName);

  return (
    <div
      data-testid="card-claim-banner"
      className="flex items-center gap-3 border-t border-border-soft bg-primary-tint px-5 py-3"
    >
      <AppIcons name="sparkle" size={20} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-base font-medium text-foreground">{title}</span>
        <span className="truncate text-meta text-muted-foreground">{subtitle}</span>
      </div>
      <Button
        variant="primary"
        size="sm"
        onClick={claim.requestClaim}
        disabled={claim.pending}
        data-testid="card-claim-banner-assume"
      >
        {t('board.drawer.action.assume')}
      </Button>
      <ClaimConfirmDialog
        open={claim.isOpen}
        pending={claim.pending}
        onConfirm={claim.confirmClaim}
        onCancel={claim.cancel}
      />
    </div>
  );
}

function bannerText(
  assigneeAgent: Actor | null,
  agentDisplayName: string | undefined,
): { title: string; subtitle: string } {
  if (assigneeAgent?.kind === 'agent') {
    return { title: t('board.claimBanner.ai.title'), subtitle: t('board.claimBanner.ai.subtitle') };
  }
  if (assigneeAgent?.kind === 'operator') {
    return {
      title: t('board.claimBanner.human.title', { name: agentDisplayName ?? '' }),
      subtitle: t('board.claimBanner.human.subtitle'),
    };
  }
  return {
    title: t('board.claimBanner.unassigned.title'),
    subtitle: t('board.claimBanner.unassigned.subtitle'),
  };
}
